/**
 * Custom Liquid Font Converter
 * Automatically converts hardcoded fonts in custom-liquid blocks to use theme font settings
 */
(function() {
  'use strict';

  /**
   * Check if an element is a heading or heading-like element
   * @param {HTMLElement} element - Element to check
   * @returns {boolean} - True if element is a heading
   */
  function isHeadingElement(element) {
    if (!element) return false;
    
    // Check if it's a heading tag
    if (/^h[1-6]$/i.test(element.tagName)) {
      return true;
    }
    
    // Check for heading-related classes
    const headingClasses = ['heading', 'title', 'headline', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
    const classList = element.classList || [];
    for (let i = 0; i < classList.length; i++) {
      const className = classList[i].toLowerCase();
      if (headingClasses.some(h => className.includes(h))) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Replace font-family in a CSS string with theme CSS variables
   * @param {string} cssText - The CSS text to process
   * @param {HTMLElement} contextElement - Optional element for context (to determine heading vs body)
   * @returns {string} - Processed CSS text
   */
  function replaceFontFamily(cssText, contextElement) {
    if (!cssText || typeof cssText !== 'string') return cssText;

    // Match font-family declarations (handles various formats)
    // Matches: font-family: "Font Name", sans-serif; or font-family: FontName, sans-serif; etc.
    const fontFamilyRegex = /font-family\s*:\s*([^;]+);?/gi;
    
    // Determine if we should use heading font (check CSS selector context)
    const isHeadingContext = contextElement ? isHeadingElement(contextElement) : 
                            /h[1-6]|\.heading|\.title|\.headline/i.test(cssText);
    
    return cssText.replace(fontFamilyRegex, function(match, fontValue) {
      // Skip if already using CSS variables
      if (fontValue.includes('var(--font-')) {
        return match;
      }
      
      const fontVar = isHeadingContext ? 'var(--font-heading-family)' : 'var(--font-body-family)';
      
      return `font-family: ${fontVar};`;
    });
  }

  /**
   * Process inline styles on elements
   * @param {HTMLElement} element - Element to process
   */
  function processInlineStyles(element) {
    if (!element || !element.hasAttribute('style')) return;

    const styleAttr = element.getAttribute('style');
    if (!styleAttr || !styleAttr.includes('font-family')) return;

    const processedStyle = replaceFontFamily(styleAttr, element);
    if (processedStyle !== styleAttr) {
      element.setAttribute('style', processedStyle);
    }
  }

  /**
   * Process style tags within the container
   * @param {HTMLElement} container - Container element
   */
  function processStyleTags(container) {
    const styleTags = container.querySelectorAll('style');
    
    styleTags.forEach(function(styleTag) {
      if (!styleTag.textContent || !styleTag.textContent.includes('font-family')) return;

      // Process CSS text rule by rule for better context detection
      const cssText = styleTag.textContent;
      
      // Split CSS into rules and process each one
      // Match CSS rules: selector { ... }
      const ruleRegex = /([^{]+)\{([^}]+)\}/g;
      let processedCss = cssText;
      let hasChanges = false;
      
      processedCss = cssText.replace(ruleRegex, function(match, selector, declarations) {
        if (!declarations.includes('font-family')) {
          return match;
        }
        
        // Check if selector targets headings
        const isHeadingSelector = /h[1-6]|\.heading|\.title|\.headline|#heading|#title|#headline/i.test(selector);
        
        // Process font-family in this rule's declarations
        const fontFamilyRegex = /font-family\s*:\s*([^;]+);?/gi;
        const processedDeclarations = declarations.replace(fontFamilyRegex, function(fontMatch, fontValue) {
          // Skip if already using CSS variables
          if (fontValue.includes('var(--font-')) {
            return fontMatch;
          }
          
          const fontVar = isHeadingSelector ? 'var(--font-heading-family)' : 'var(--font-body-family)';
          hasChanges = true;
          return `font-family: ${fontVar};`;
        });
        
        if (processedDeclarations !== declarations) {
          return selector + '{' + processedDeclarations + '}';
        }
        
        return match;
      });
      
      if (hasChanges) {
        styleTag.textContent = processedCss;
      }
    });
  }

  /**
   * Process all elements within a custom-liquid block
   * @param {HTMLElement} container - The custom-liquid block container
   */
  function processCustomLiquidBlock(container) {
    if (!container) return;

    // Process all elements with inline styles
    const elementsWithStyles = container.querySelectorAll('[style*="font-family"]');
    elementsWithStyles.forEach(processInlineStyles);

    // Also process the container itself if it has inline styles
    processInlineStyles(container);

    // Process style tags
    processStyleTags(container);
  }

  /**
   * Initialize the font converter
   */
  function init() {
    // Find all custom-liquid blocks
    const customLiquidBlocks = document.querySelectorAll('.custom-liquid-block');
    
    if (customLiquidBlocks.length === 0) return;

    customLiquidBlocks.forEach(function(block) {
      processCustomLiquidBlock(block);
    });
  }

  // Run on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already loaded
    init();
  }

  // Also run when Shopify theme editor updates content
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
    document.addEventListener('shopify:section:reorder', init);
  }
})();
